import Paper from '@/components/Paper'
import {
    DataTableColumn,
    DataTableCustomized,
    createDateFormatter,
    createInternalLinkFormatter,
} from '@/components/ui/data-table-customized'
import { Badge } from '@/components/ui/badge'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getServiceSLAs } from '@/modules/Service/services/sla'
import { ServiceSLA } from '@/modules/Service/types/sla'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const ServicesWithSLA = () => {
    const { t } = useTranslation()
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(20)

    const { data, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES, 'sla-registry', page, pageSize],
        queryFn: () =>
            getServiceSLAs({
                limit: pageSize,
                offset: (page - 1) * pageSize,
            }),
    })

    const items = (data?.results || data?.result?.data || []) as ServiceSLA[]
    const total = (data?.result?.total ||
        data?.pagination?.total ||
        0) as number

    const columns = useMemo<DataTableColumn<ServiceSLA>[]>(
        () => [
            {
                key: 'service_id',
                title: t('service_id'),
                width: 80,
                pinned: 'left',
                formatter: createInternalLinkFormatter({
                    pathKey: 'serviceDetail',
                    pathParams: (value) => ({ id: String(value) }),
                }),
            },
            {
                key: 'product_group_name',
                title: t('product_group_name'),
                width: 250,
                cellClassName: 'truncate',
            },
            {
                key: 'service_name',
                title: t('service_name'),
                width: 360,
                cellClassName: 'uppercase truncate',
            },
            {
                key: 'customer_name',
                title: t('service_customer'),
                width: 320,
                cellClassName: 'uppercase truncate',
                formatter: createInternalLinkFormatter({
                    pathKey: 'customerDetail',
                    pathParams: (_, row) => ({
                        id: String(row?.customer_id || ''),
                    }),
                    label: (_, row) =>
                        row?.company_name || row?.customer_name || '-',
                }),
            },
            {
                key: 'template_name',
                title: t('services:sla-template'),
                width: 200,
                cellClassName: 'truncate',
                render: (_v, row) => row.template_name || '-',
            },
            {
                key: 'template_tier',
                title: t('services:sla-tier'),
                width: 120,
                cellClassName: 'uppercase',
                render: (_v, row) =>
                    row.template_tier?.sla_tier || row.sla_source || 'manual',
            },
            {
                key: 'sale_followers',
                title: t('services:sale-followers'),
                width: 200,
                cellClassName: 'truncate',
                render: (_v, row) =>
                    row.sale_followers && row.sale_followers.length > 0
                        ? row.sale_followers.join(', ')
                        : '-',
            },
            {
                key: 'cs_followers',
                title: t('services:cs-followers'),
                width: 200,
                cellClassName: 'truncate',
                render: (_v, row) =>
                    row.cs_followers && row.cs_followers.length > 0
                        ? row.cs_followers.join(', ')
                        : '-',
            },
            {
                key: 'is_active',
                title: t('status'),
                width: 170,
                render: (_v, row) =>
                    row.is_active ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200">
                            {t('services:sla-active')}
                        </Badge>
                    ) : (
                        <Badge variant="outline" className="text-gray-500">
                            {t('services:sla-inactive')}
                        </Badge>
                    ),
            },
            {
                key: 'effective_from',
                title: t('services:sla-effective-from'),
                width: 130,
                formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
            },
            {
                key: 'effective_to',
                title: t('services:sla-effective-to'),
                width: 130,
                render: (_v, row) =>
                    row.effective_to
                        ? new Date(row.effective_to).toLocaleDateString('vi-VN')
                        : t('services:sla-indefinite'),
            },
            {
                key: 'service_start_date',
                title: t('service_start_date'),
                width: 130,
                formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
            },
            {
                key: 'service_stopped_date',
                title: t('service_stopped_date'),
                width: 130,
                render: (_v, row) =>
                    row.service_stopped_date
                        ? new Date(row.service_stopped_date).toLocaleDateString(
                              'vi-VN'
                          )
                        : '-',
            },
        ],
        [t]
    )

    return (
        <Paper className="my-2">
            <DataTableCustomized<ServiceSLA>
                columns={columns}
                rows={items}
                rowKey={(row) => String(row.service_sla_id)}
                size="small"
                resizable
                loading={!items.length && isFetching}
                isFetching={isFetching}
                emptyMessage={t('services:no-services-with-sla')}
                pagination={{
                    page,
                    pageSize,
                    total,
                    onPageChange: setPage,
                    onPageSizeChange: (size) => {
                        setPageSize(size)
                        setPage(1)
                    },
                    pageSizeOptions: [10, 20, 50, 100],
                }}
                stickyHeader={{
                    enabled: true,
                    offsetTopSelector: '#main-header',
                }}
            />
        </Paper>
    )
}

export default ServicesWithSLA
